import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert, Tabs, Tab } from 'react-bootstrap';
import authService from '../services/authService';

function Admin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [feedbackSummary, setFeedbackSummary] = useState(null);

  // Modals
  const [showTierModal, setShowTierModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTier, setSelectedTier] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    if (!authResult.user?.is_admin) {
      navigate('/dashboard');
      return;
    }

    await loadData();
  };

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [usersRes, statsRes, feedbackRes] = await Promise.all([
        fetch('/api/admin/users', { credentials: 'include' }),
        fetch('/api/admin/stats', { credentials: 'include' }),
        fetch('/api/admin/feedback/summary', { credentials: 'include' })
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats);
      }

      if (feedbackRes.ok) {
        const data = await feedbackRes.json();
        setFeedbackSummary(data);
      }
    } catch (err) {
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const openTierModal = (user) => {
    setSelectedUser(user);
    setSelectedTier(user.command_center_tier || '');
    setShowTierModal(true);
  };

  const saveTier = async () => {
    if (!selectedUser) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/admin/users/${selectedUser.id}/tier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tier: selectedTier || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Updated ${selectedUser.email} to ${selectedTier || 'Free'} tier. ${data.ghl_result?.message || ''}`);
        setShowTierModal(false);
        loadData();
      } else {
        setError(data.error || 'Failed to update tier');
      }
    } catch (err) {
      setError('Failed to update tier');
    } finally {
      setSaving(false);
    }
  };

  const provisionGHL = async (userId) => {
    if (!window.confirm('Provision GHL account for this user? They will receive login credentials via email.')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/admin/users/${userId}/provision-ghl`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'GHL account provisioned');
        loadData();
      } else {
        setError(data.error || 'Failed to provision GHL');
      }
    } catch (err) {
      setError('Failed to provision GHL');
    }
  };

  const getTierBadge = (tier) => {
    const colors = {
      'essential': 'info',
      'advanced': 'primary',
      'ultimate': 'warning',
      'black': 'dark'
    };
    return tier ? (
      <Badge bg={colors[tier] || 'secondary'}>{tier}</Badge>
    ) : (
      <Badge bg="secondary">Free</Badge>
    );
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">Loading...</div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Admin Dashboard</h1>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Link to="/admin/stats" className="btn btn-outline-success">
            Product Analytics
          </Link>
          <Link to="/admin/roadmap" className="btn btn-outline-primary">
            Manage Roadmap
          </Link>
          <Link to="/feedback-admin" className="btn btn-outline-primary">
            View All Feedback
          </Link>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats Cards */}
      {stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3>{stats.total_users}</h3>
                <div className="text-muted">Total Users</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3>{stats.ghl_provisioned}</h3>
                <div className="text-muted">GHL Accounts</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3>{stats.sites_connected}</h3>
                <div className="text-muted">Sites Connected</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3>{feedbackSummary?.stats?.new || 0}</h3>
                <div className="text-muted">New Feedback</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tier Breakdown */}
      {stats && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>Tier Distribution</Card.Header>
              <Card.Body>
                <Row>
                  <Col><strong>Free:</strong> {stats.tiers.free}</Col>
                  <Col><strong>Essential:</strong> {stats.tiers.essential}</Col>
                  <Col><strong>Advanced:</strong> {stats.tiers.advanced}</Col>
                  <Col><strong>Ultimate:</strong> {stats.tiers.ultimate}</Col>
                  <Col><strong>Black:</strong> {stats.tiers.black}</Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Tabs defaultActiveKey="users" className="mb-4">
        <Tab eventKey="users" title="Users">
          <Card>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Business</th>
                    <th>Tier</th>
                    <th>GHL</th>
                    <th>Site</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        {user.email}
                        {user.is_admin && <Badge bg="danger" className="ms-2">Admin</Badge>}
                      </td>
                      <td>{user.business_name || user.owner_name || '-'}</td>
                      <td>{getTierBadge(user.command_center_tier)}</td>
                      <td>
                        {user.ghl_location_id ? (
                          <Badge bg="success">Provisioned</Badge>
                        ) : (
                          <Badge bg="secondary">None</Badge>
                        )}
                      </td>
                      <td>
                        {user.site_url ? (
                          <a href={user.site_url} target="_blank" rel="noopener noreferrer">
                            Connected
                          </a>
                        ) : '-'}
                      </td>
                      <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => openTierModal(user)}
                        >
                          Set Tier
                        </Button>
                        {!user.ghl_location_id && user.command_center_tier && (
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => provisionGHL(user.id)}
                          >
                            Provision GHL
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="feedback" title={`Feedback (${feedbackSummary?.stats?.new || 0} new)`}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Recent Feedback</span>
              <Link to="/feedback-admin" className="btn btn-sm btn-primary">
                View All
              </Link>
            </Card.Header>
            <Card.Body>
              {feedbackSummary?.recent?.length > 0 ? (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbackSummary.recent.map(fb => (
                      <tr key={fb.id}>
                        <td><Badge bg="info">{fb.type}</Badge></td>
                        <td>{fb.message}</td>
                        <td>
                          <Badge bg={fb.status === 'new' ? 'warning' : fb.status === 'resolved' ? 'success' : 'primary'}>
                            {fb.status}
                          </Badge>
                        </td>
                        <td>{fb.created_at ? new Date(fb.created_at).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No recent feedback</p>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Tier Modal */}
      <Modal show={showTierModal} onHide={() => setShowTierModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Set Command Center Tier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p><strong>User:</strong> {selectedUser.email}</p>
              <p><strong>Current Tier:</strong> {selectedUser.command_center_tier || 'Free'}</p>

              <Form.Group className="mb-3">
                <Form.Label>New Tier</Form.Label>
                <Form.Select
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value)}
                >
                  <option value="">Free (Profit Map only)</option>
                  <option value="essential">Essential</option>
                  <option value="advanced">Advanced</option>
                  <option value="ultimate">Ultimate</option>
                  <option value="black">Black</option>
                </Form.Select>
              </Form.Group>

              {selectedTier && !selectedUser.ghl_location_id && (
                <Alert variant="info">
                  A GHL sub-account will be created for this user. They will receive their login credentials via email.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTierModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveTier} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Admin;
